import { cancelSelectEvent, getCSSStyles, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, getEChartsTooltipTheme, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface TradeAmountItem {
    name: string;
    value: number;
    color: string;
}

const AMOUNT_COLORS = [
    '#8B5CF6', '#2563EB', '#12C9A0', '#FF2D9B', '#FFC01E', '#FF7A1A'
];

export class TraderverseCongressTradeAmountDistribution {
    private container: HTMLElement;
    private config: WidgetConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private currentData: TradeAmountItem[] = [];
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private hiddenRanges: Set<string> = new Set();

    constructor(config: WidgetConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<WidgetConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    /** Sample data used until the trade amount distribution API is available. */
    private getDummyData(): TradeAmountItem[] {
        return [
            { name: '$1K - $15K', value: 248, color: AMOUNT_COLORS[0] },
            { name: '$15K - $50K', value: 208, color: AMOUNT_COLORS[1] },
            { name: '$50K - $100K', value: 142, color: AMOUNT_COLORS[2] },
            { name: '$100K - $250K', value: 152, color: AMOUNT_COLORS[3] },
            { name: '$250K - $500K', value: 98, color: AMOUNT_COLORS[4] },
            { name: '$500K+', value: 62, color: AMOUNT_COLORS[5] }
        ];
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Congress Trade Amount Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <div class="legendsCollection" id="tradeAmountLegends"></div>
                    </div>
                    <span></span>
                </div>
                <div class="chartDv" id="tradeAmountContainer">
                    <div id="tradeAmountChart" class="chart coverDv" data-testing-id="congress-trade-amount-distribution-chart"></div>
                </div>
            </div>
        </div>`;
        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        setupHeaderPin(this.shadowRoot, this.config);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
            script.async = true;
            script.onload = () => resolve(true);
            this.shadowRoot.appendChild(script);
        });
    }

    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            if (widget) widget.classList.add('loading');
            this.currentData = this.getDummyData();
            this.hiddenRanges.clear();
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData.length || !(window as any).echarts) return;
        this.drawChart();
        this.renderLegends();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#tradeAmountChart') as HTMLElement;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const visible = this.currentData.filter(item => !this.hiddenRanges.has(item.name));
        if (!visible.length) {
            container.innerHTML = getNoDataHTML();
            return;
        }
        container.innerHTML = '';

        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');

        const option = {
            backgroundColor: 'transparent',
            legend: { show: false },
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (p: any) => `
                  <div style="min-width:90px">
                    <div style="font-weight:700;font-size:15px">${Number(p.percent).toFixed(1)}%</div>
                    <div style="opacity:.7">${p.name}</div>
                  </div>
                `
            },
            series: [
                {
                    type: 'pie',
                    radius: ['42%', '92%'],
                    center: ['50%', '50%'],
                    startAngle: 90,
                    minAngle: 4,
                    padAngle: 1.5,
                    avoidLabelOverlap: true,
                    itemStyle: {
                        borderColor: bgColor,
                        borderWidth: 2
                    },
                    label: {
                        show: true,
                        position: 'inside',
                        formatter: '{d}%',
                        color: '#FFFFFF',
                        fontWeight: 600,
                        fontSize: fontSize
                    },
                    labelLine: { show: false },
                    emphasis: {
                        scale: false,
                        itemStyle: { shadowBlur: 12, shadowColor: 'rgba(0, 0, 0, 0.35)' }
                    },
                    data: visible.map(item => ({
                        name: item.name,
                        value: item.value,
                        itemStyle: { color: item.color }
                    }))
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
        requestAnimationFrame(() => this.currentChart?.resize());
    }

    private renderLegends(): void {
        const container = this.shadowRoot.querySelector('#tradeAmountLegends') as HTMLElement;
        if (!container) return;

        container.innerHTML = '';

        this.currentData.forEach(item => {
            const el = document.createElement('span');
            const isActive = !this.hiddenRanges.has(item.name);
            el.className = `toggle-series${isActive ? ' active' : ''}`;
            el.style.backgroundColor = item.color;
            el.setAttribute('data-tooltip', item.name);
            el.title = item.name;

            el.addEventListener('click', () => {
                const hide = !this.hiddenRanges.has(item.name);
                if (hide) {
                    this.hiddenRanges.add(item.name);
                } else {
                    this.hiddenRanges.delete(item.name);
                }
                el.classList.toggle('active', !hide);
                this.drawChart();
            });

            container.appendChild(el);
        });
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseCongressTradeAmountDistribution: typeof TraderverseCongressTradeAmountDistribution; } }
if (typeof window !== 'undefined') window.TraderverseCongressTradeAmountDistribution = TraderverseCongressTradeAmountDistribution;
